import { createSelector } from '@reduxjs/toolkit';
import { FileFolderType, FolderRootPath } from '@qodly/shared';
import { ITab } from '@qodly/shared';
import { get } from 'lodash';
import { AppState } from '../modules';

export const selectRoot = createSelector(
  (state: AppState) => state.root,
  (root) => root,
);

export const selectTabs = createSelector(selectRoot, (root) => root.tabs);

export const selectActiveTab = createSelector(
  selectRoot,
  (root) => root.activeTab,
);

export const selectRecentFiles = createSelector(selectRoot, (root) =>
  root.recent.filter(({ type }) => type !== FileFolderType.DEBUGGER),
);

export const selectCurrentTab = createSelector(
  selectRoot,
  ({ tabs, activeTab }) => tabs.find(({ path }) => path === activeTab),
);

export const selectTabMaxWeight = createSelector(selectRoot, ({ tabs }) => {
  const result = tabs.reduce<ITab | null>((prev, cur) => {
    if (cur?.weight! > (prev?.weight || 0)) {
      return cur;
    }
    return prev;
  }, null);

  return result;
});

export const selectPreviewedWebformsPaths = createSelector(selectTabs, (tabs) =>
  tabs
    .filter(
      ({ type, flags }) =>
        type === FileFolderType.WEBFORM && flags.enabled === false,
    )
    .map(({ path }) => path),
);

export const selectHasTabs = createSelector(
  selectTabs,
  (tabs) => tabs.length > 0,
);

export const selectTabByPath = (tabPath: string) =>
  createSelector(selectRoot, (root) =>
    root.tabs.find(({ path }) => path === tabPath),
  );

export const selectTabNameByPath = (tabPath: string) =>
  createSelector(selectTabByPath(tabPath), (tab) => tab?.name);

export const selectContentByPath = (tabPath: string, contentPath = '') =>
  createSelector(selectTabByPath(tabPath), (tab) => {
    if (!tab || !tab.content || typeof tab.content === 'string') {
      return tab?.content;
    }

    return get(tab.content, contentPath);
  });

export const selectTabExtraByPath = (tabPath: string) =>
  createSelector(selectTabByPath(tabPath), (tab) => tab?.extra);

export const selectWebformNodes = (
  path: string,
  transformer: (value: object) => any = (value) => value,
) =>
  createSelector(selectContentByPath(path, 'components'), (components) =>
    transformer(components || {}),
  );

export const selectTouchedTabs = createSelector(selectTabs, (tabs) =>
  tabs.filter((tab) => tab.flags.touched),
);

export const selectTouchedTabsLength = createSelector(
  selectTouchedTabs,
  (touchedTabs) => touchedTabs.length,
);

export const selectExplorer = createSelector(
  (state: AppState) => state,
  (state) => state.explorer || [],
);

export const selectWebFormsFolder = createSelector(
  (state: AppState) => state,
  (state) =>
    state.explorer.find((item) => item.path === FolderRootPath.WEBFORMS),
);

export const selectMethodsFolder = createSelector(
  (state: AppState) => state,
  (state) =>
    state.explorer.find((item) => item.path === FolderRootPath.METHODS),
);

export const selectClassesFolder = createSelector(
  (state: AppState) => state,
  (state) =>
    state.explorer.find((item) => item.path === FolderRootPath.CLASSES),
);

export const selectMethods = createSelector(
  selectMethodsFolder,
  (folder) => folder?.children || [],
);

export const selectWebForms = createSelector(
  selectWebFormsFolder,
  (folder) => folder?.children?.filter((file) => file.ext === 'WebForm') || [],
);

export const selectClasses = createSelector(
  selectClassesFolder,
  (folder) => folder?.children || [],
);
